'use client';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { usePathname, useRouter, useSearchParams } from '@/lib/app-router';
import { Header } from '@/components/layout/header/Header';
import { Footer } from '@/components/layout/footer/Footer';
import { MOBILE_MENU_ITEMS } from '@/lib/constants/NavItems';
import { useWhiteLabelNav } from '@/hooks/useWhiteLabelNav';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';
import { resolveLogoSrc, getCompanyBookingEnablement } from '@/lib/theme-utils';
import { getContactName } from '@/lib/contact-utils';
import { isLoyaltyVisible } from '@/lib/loyalty-utils';
import { isScanEntryVisible } from '@/lib/special-rate-utils';
import { getBase64QRCode } from '@/lib/qr-code';
import { useLoyaltyApplePassUrl } from '@/hooks/shared/useLoyaltyApplePassUrl';
import { MY_ACCOUNT_ROUTES, isQrScanPath } from '@/routes/routes.constants';
import { useUserMenu } from '@/hooks/useUserMenu';
import { useLoginHref } from '@/hooks/useLoginHref';
import { useActiveSessionTimer } from '@/hooks/useActiveSessionTimer';
import { UserMenu } from '@/components/layout/user-menu';
import { LoyaltyQrModal } from '@/components/myaccount/loyalty-qr-modal';
import { ViewQRDialog } from '@/components/myaccount/manage-permit/permit-details/ViewQRDialog';
import { useWidgetStore } from '@/stores/widget.store';
import { MobileNavigation } from '@/components/myaccount/account-navigation/MobileNavigation';
import { FindSessionProvider, useFindSessionContext } from './FindSessionContext';
import { FindSessionModal } from '@/components/landing/find-session-modal';

function PublicLayoutInner({ children }: { children: React.ReactNode }) {
  const { theme } = useThemeStore();
  const companyThemes = useThemeStore((s) => s.companyThemes);
  const { user, token, garageAccounts } = useAuthStore();
  const router = useRouter();
  const findSession = useFindSessionContext();

  // Active session indicator
  const { sessions: activeSessions, indicator: activeSessionIndicator } = useActiveSessionTimer();
  const [sessionsPopupOpen, setSessionsPopupOpen] = useState(false);
  const handleOpenSessionsPopup = useCallback(() => setSessionsPopupOpen(true), []);
  const handleCloseSessionsPopup = useCallback(() => setSessionsPopupOpen(false), []);

  const indicatorProps = useMemo(() => {
    if (!activeSessionIndicator) return null;
    return {
      ...activeSessionIndicator,
      href: undefined,
      onClick: handleOpenSessionsPopup,
    };
  }, [activeSessionIndicator, handleOpenSessionsPopup]);

  const popupProps = useMemo(() => {
    if (!activeSessionIndicator) return null;
    return {
      open: sessionsPopupOpen,
      onClose: handleCloseSessionsPopup,
      sessions: activeSessions,
    };
  }, [activeSessionIndicator, sessionsPopupOpen, handleCloseSessionsPopup, activeSessions]);

  const getDefaultWidget = useWidgetStore((state) => state.getDefaultWidget);

  useEffect(() => {
    getDefaultWidget();
  }, [getDefaultWidget]);
  const pathname = usePathname();
  const isPaymentPage = false;
  const hideFooter = isPaymentPage || pathname.startsWith('/quote/');
  // Header + footer follow the company's back-office white-label tabs.
  const { headerNavItems, footerLinks } = useWhiteLabelNav();

  const logoSrc = resolveLogoSrc(theme?.logo);
  const isLoggedIn = !!user;

  /**
   * Where the app-shell logo navigates — the desktop half of the QR logo fix (PB-4971).
   *
   * ## Invariants:
   * - On a QR scan route it points at the CURRENT scan url, QUERY STRING INCLUDED, so a parker who
   *   taps the logo restarts that scan instead of landing on the marketing home page — a dead end,
   *   because the QR code is printed on a sign in the garage rather than held on their phone. The
   *   query carries the company and direction; without it the scan cannot resolve its company.
   * - Every other route keeps `/`, unchanged.
   * - Header's own same-path handler turns this into a full re-entry of the url, which is also
   *   what closes the QR payment screen on desktop — that screen is state over the same url, and
   *   a reload drops it. Mobile cannot use that route (the brand header covers the app shell) and
   *   closes the screen explicitly via `useQrBookingPage`'s `onLogoClick`.
   */
  const searchParams = useSearchParams();
  const logoHref = useMemo(() => {
    if (!isQrScanPath(pathname)) return '/';
    const query = searchParams?.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);

  /**
   * Guest "Sign In" for the app-shell header — desktop nav AND the mobile guest action.
   *
   * Was a module-level constant holding a bare `/login`, which is why signing in from the header
   * on a scan or touchless surface dropped the parker on /myaccount instead of the page they left
   * (PB-4971). It has to be built per render because the return URL is the CURRENT location.
   */
  const loginHref = useLoginHref();
  const publicSecondaryNav = useMemo(
    () => [{ labelKey: 'signIn', label: 'Sign In', href: loginHref }],
    [loginHref]
  );

  const [showLoyaltyQr, setShowLoyaltyQr] = useState(false);
  const handleOpenLoyaltyQr = useCallback(() => setShowLoyaltyQr(true), []);
  const handleCloseLoyaltyQr = useCallback(() => setShowLoyaltyQr(false), []);

  const [showAccessCardQr, setShowAccessCardQr] = useState(false);
  const handleOpenAccessCardQr = useCallback(() => setShowAccessCardQr(true), []);
  const handleCloseAccessCardQr = useCallback(() => setShowAccessCardQr(false), []);

  const firstBarcodeCredential = useMemo(() => {
    for (const ga of garageAccounts) {
      for (const permit of ga.monthlyAccount?.monthlyPermits ?? []) {
        const cred = permit.accessCredentials?.find((c) => c.credentialType === 'BARCODE');
        if (cred) {
          return {
            cardNumber: cred.serialNumber || cred.cardNumber || '',
            description: cred.description ?? '',
          };
        }
      }
    }
    return null;
  }, [garageAccounts]);

  const userMenu = useUserMenu({
    onScanTicket: () => findSession.openModal('scanQr', 'scan'),
    onLoyaltyQr: handleOpenLoyaltyQr,
    onAccessCardQr: handleOpenAccessCardQr,
  });

  const userDisplayName = isLoggedIn ? getContactName(user?.contactInfo) || 'User' : undefined;

  const loyaltyEnabled = isLoggedIn && isLoyaltyVisible(user, theme);
  const showScanTicket = isScanEntryVisible(user, theme, companyThemes);
  const cardNumber = user?.cardNumber || '';
  const lcnDisplay =
    cardNumber && cardNumber !== '-'
      ? cardNumber.toUpperCase().startsWith('LCN-')
        ? cardNumber
        : `LCN-${cardNumber}`
      : '-';
  const largeQrSrc = useMemo(() => {
    if (!showLoyaltyQr || !cardNumber || cardNumber === '-') return '';
    return getBase64QRCode(cardNumber, 'M', 250, 15);
  }, [showLoyaltyQr, cardNumber]);

  const applePassUrl = useLoyaltyApplePassUrl(token);

  const googlePassJwt = useMemo(() => {
    const companyId = theme?.companyId;
    if (!user?.googlePassTokenMap || !companyId) return undefined;
    return user.googlePassTokenMap[companyId];
  }, [user, theme?.companyId]);

  return (
    <div className="tw:flex tw:flex-col tw:h-dvh tw:overflow-hidden">
      {!isPaymentPage && (
        <Header
          logo={<img src={logoSrc} alt={theme?.companyName || 'Company logo'} className="tw:h-8" />}
          logoHref={logoHref}
          primaryNavItems={headerNavItems}
          secondaryNavItems={isLoggedIn ? [] : publicSecondaryNav}
          mobileGuestAction={publicSecondaryNav[0]}
          user={isLoggedIn ? { name: userDisplayName! } : undefined}
          onAvatarClick={isLoggedIn ? userMenu.toggle : undefined}
          avatarRef={isLoggedIn ? userMenu.anchorRef : undefined}
          isMenuOpen={userMenu.isOpen}
          activeSessionIndicator={isLoggedIn ? indicatorProps : null}
          activeSessionsPopup={isLoggedIn ? popupProps : null}
          className="tw:sticky tw:top-0 tw:z-20 tw:scan:z-50 tw:flex-none"
        />
      )}
      {isLoggedIn && (
        <UserMenu
          open={userMenu.isOpen}
          userName={userDisplayName!}
          onClose={userMenu.close}
          onProfileClick={userMenu.handleProfileClick}
          showAccessCardQr={!!firstBarcodeCredential}
          showLoyaltyQr={loyaltyEnabled}
          showScanTicket={showScanTicket}
          onAccessCardQrClick={userMenu.handleAccessCardQrClick}
          onLoyaltyQrClick={userMenu.handleLoyaltyQrClick}
          onScanTicketClick={userMenu.handleScanTicketClick}
          onOverviewClick={userMenu.handleOverviewClick}
          onChangeAppearanceClick={userMenu.handleChangeAppearanceClick}
          onLogoutClick={userMenu.handleLogoutClick}
          anchorRef={userMenu.anchorRef}
        />
      )}
      <main
        id="main-content"
        className="tw:flex-1 tw:flex tw:flex-col tw:relative tw:min-h-0 tw:overflow-hidden"
      >
        {children}
      </main>
      {isLoggedIn && !hideFooter && (
        <MobileNavigation
          items={MOBILE_MENU_ITEMS.filter(
            (item) => item.id !== 'reservations' || getCompanyBookingEnablement(theme).hourly
          )}
          activeItemId="home"
          onItemClick={(itemId) => {
            switch (itemId) {
              case 'home':
                router.push('/');
                break;
              case 'reservations':
                router.push(MY_ACCOUNT_ROUTES.reservations);
                break;
              case 'account':
                router.push(MY_ACCOUNT_ROUTES.overview);
                break;
            }
          }}
          className="tw:md:hidden tw:flex-shrink-0"
        />
      )}
      {!hideFooter && (
        <Footer
          companyName={theme?.companyName ?? ''}
          links={footerLinks}
          className="tw:hidden tw:scan:flex tw:flex-none"
        />
      )}
      {loyaltyEnabled && showLoyaltyQr && (
        <LoyaltyQrModal
          logo={theme?.logo}
          companyName={theme?.companyName}
          userName={userDisplayName!}
          lcnDisplay={lcnDisplay}
          largeQrSrc={largeQrSrc}
          onClose={handleCloseLoyaltyQr}
          applePassUrl={applePassUrl}
          googlePassJwt={googlePassJwt}
        />
      )}
      {isLoggedIn && firstBarcodeCredential && showAccessCardQr && (
        <ViewQRDialog
          open={showAccessCardQr}
          onClose={handleCloseAccessCardQr}
          description={firstBarcodeCredential.description}
          cardNumber={firstBarcodeCredential.cardNumber}
        />
      )}
      {isLoggedIn && (
        <FindSessionModal
          open={findSession.open}
          onClose={findSession.closeModal}
          initialTab={findSession.initialTab}
          entry={findSession.entry}
        />
      )}
    </div>
  );
}

export default function PublicLayoutClient({ children }: { children: React.ReactNode }) {
  return (
    <FindSessionProvider>
      <PublicLayoutInner>{children}</PublicLayoutInner>
    </FindSessionProvider>
  );
}
